"use client"

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import useElectron from '@/hooks/useElectron'

// Type pour la configuration principale
export interface AppConfig {
  width: number
  height: number
  gridSize: number
  rotationDeg: 0 | 90 | 180 | -90 | -180
  animationSpeed: number
  sonarSpeed: number
  patternSeed: number
  exportFps: number
  exportFormat: 'webm' | 'mp4' | 'mov'
  showGrid: boolean
  gridEvenOpacity: number
  gridOddOpacity: number
  patternType: string
  standardPatternType: string
  rampDirection: string
  backgroundColor: string
  showResolution: boolean
  resolutionFontSize: number
  showLEDNumbers: boolean
  showTechnicalDetails: boolean // Nouvelle option unifiée
  showLogo: boolean
  showSonar: boolean
  sonarThickness: number
  sonarMode: boolean // Enable Advanced Sonar mode
  showVisualEffects: boolean // Nouvelle option unifiée Logo + Sonar
  showDefaultLogo: boolean // Option pour afficher le logo VisualGrid au centre
  ledWallMode: {
    enabled: boolean
    columns: number
    rows: number
    panelWidth: number
    panelHeight: number
    borderWidth: number
  }
  sonarRevealMode: boolean
  allowFullscreenScroll: boolean
  stretchFullscreen: boolean
  gridOffset: {
    x: number
    y: number
  }
  pixelMapMode: {
    enabled: boolean
    mainScreenText: string
    posX: number
    posY: number
    resW: number
    resH: number
    panelW: number
    panelH: number
    size: number
  }
  novaScreenConfigMode: boolean // Mode Nova Screen Config pour numérotation personnalisée
  novaShowArrows: boolean // Afficher les flèches entre les connexions
  novaShowLines: boolean // Afficher les lignes entre les connexions
  novaMinimalMode: boolean // Afficher les numérotations Nova en mode minimal
  enableAnamorphosis: boolean // Mode anamorphose pour compensation LST
}

// Type pour la configuration du multi-grid
export interface MultiGridCanvasConfig {
  width: number
  height: number
  allowFullscreenScroll: boolean
  stretchFullscreen: boolean
  canvasPattern: string // 'none' | any patternType value
  canvasAnimationSpeed: number
  canvasPatternSeed: number
  canvasGradientLoopDuration: number
}

// Configuration par défaut pour la page principale
const defaultMainConfig: AppConfig = {
  width: 1920,
  height: 1152,
  gridSize: 192,
  rotationDeg: 0,
  animationSpeed: 0.8,
  sonarSpeed: 0.2,
  patternSeed: 0,
  exportFps: 60,
  exportFormat: 'webm', // WebM par défaut (instantané, pas de conversion)
  showGrid: true,
  gridEvenOpacity: 97,
  gridOddOpacity: 85,
  patternType: "gradient",
  standardPatternType: "none",
  rampDirection: "horizontal",
  backgroundColor: "#ff0000",
  showResolution: true,
  resolutionFontSize: 24,
  showLEDNumbers: true,
  showTechnicalDetails: true, // Nouvelle option unifiée
  showLogo: true,
  showSonar: true,
  sonarThickness: 4,
  sonarMode: false, // Disabled by default, enable Advanced Sonar mode when needed
  showVisualEffects: true, // Nouvelle option unifiée Logo + Sonar
  showDefaultLogo: true, // Afficher le logo VisualGrid au centre par défaut
  ledWallMode: {
    enabled: true,
    columns: 10,
    rows: 6,
    panelWidth: 192,
    panelHeight: 192,
    borderWidth: 2
  },
  sonarRevealMode: false,
  allowFullscreenScroll: false,
  stretchFullscreen: false,
  gridOffset: {
    x: 0,
    y: 0
  },
  pixelMapMode: {
    enabled: false,
    mainScreenText: 'MAIN SCREEN',
    posX: 0,
    posY: 0,
    resW: 1920,
    resH: 1152,
    panelW: 192,
    size: 1.0,
    panelH: 192
  },
  novaScreenConfigMode: false, // Mode Nova Screen Config pour numérotation personnalisée
  novaShowArrows: true, // Afficher les flèches entre les connexions
  novaShowLines: true, // Afficher les lignes entre les connexions
  novaMinimalMode: false, // Afficher les numérotations Nova en mode minimal
  enableAnamorphosis: false // Mode anamorphose pour compensation LST
}

// Configuration par défaut pour le multi-grid
const defaultMultiGridConfig: MultiGridCanvasConfig = {
  width: 1920,
  height: 1080,
  allowFullscreenScroll: false,
  stretchFullscreen: false,
  canvasPattern: 'none',
  canvasAnimationSpeed: 1,
  canvasPatternSeed: 0,
  canvasGradientLoopDuration: 10
}

interface ConfigContextType {
  // Configuration principale
  mainConfig: AppConfig
  setMainConfig: (config: AppConfig | ((prev: AppConfig) => AppConfig)) => void
  
  // Configuration multi-grid
  multiGridConfig: MultiGridCanvasConfig
  setMultiGridConfig: (config: MultiGridCanvasConfig | ((prev: MultiGridCanvasConfig) => MultiGridCanvasConfig)) => void
  
  // Méthodes utilitaires
  resetMainConfig: () => void
  resetMultiGridConfig: () => void
  saveCurrentConfig: (name?: string) => Promise<void>
  loadSavedConfig: (name?: string) => Promise<void>
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined)

const CONFIG_STORAGE_KEY = 'visual-grid-current-config'
const MULTIGRID_CONFIG_STORAGE_KEY = 'visual-grid-multigrid-config'

export function ConfigProvider({ children }: { children: React.ReactNode }) {
  const [mainConfig, setMainConfigState] = useState<AppConfig>(defaultMainConfig)
  const [multiGridConfig, setMultiGridConfigState] = useState<MultiGridCanvasConfig>(defaultMultiGridConfig)
  const [mounted, setMounted] = useState(false)
  const { loadConfig, saveConfig } = useElectron()

  // Charger la configuration depuis le stockage au montage
  useEffect(() => {
    const loadStoredConfig = async () => {
      try {
        // Charger la configuration principale
        const mainConfigStr = localStorage.getItem(CONFIG_STORAGE_KEY)
        if (mainConfigStr) {
          const storedMainConfig = JSON.parse(mainConfigStr)
          // Fusionner avec les valeurs par défaut pour assurer que les nouvelles propriétés sont présentes
          const mergedConfig = { ...defaultMainConfig, ...storedMainConfig }
          setMainConfigState(mergedConfig)
          // Sauvegarder immédiatement la configuration fusionnée pour persister les nouvelles valeurs par défaut
          localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(mergedConfig))
        }

        // Charger la configuration multi-grid
        const multiGridConfigStr = localStorage.getItem(MULTIGRID_CONFIG_STORAGE_KEY)
        if (multiGridConfigStr) {
          const storedMultiGridConfig = JSON.parse(multiGridConfigStr)
          // Fusionner avec les valeurs par défaut pour assurer que les nouvelles propriétés sont présentes
          const mergedMultiGridConfig = { ...defaultMultiGridConfig, ...storedMultiGridConfig }
          setMultiGridConfigState(mergedMultiGridConfig)
          // Sauvegarder immédiatement la configuration fusionnée pour persister les nouvelles valeurs par défaut
          localStorage.setItem(MULTIGRID_CONFIG_STORAGE_KEY, JSON.stringify(mergedMultiGridConfig))
        }
      } catch (error) {
        // console.error('Erreur lors du chargement de la configuration:', error)
      }
      setMounted(true)
    }

    loadStoredConfig()
  }, [])

  // Sauvegarder automatiquement la configuration principale lors des changements
  useEffect(() => {
    if (mounted) {
      try {
        localStorage.setItem(CONFIG_STORAGE_KEY, JSON.stringify(mainConfig))
      } catch (error) {
        // console.error('Erreur lors de la sauvegarde de la configuration principale:', error)
      }
    }
  }, [mainConfig, mounted])

  // Sauvegarder automatiquement la configuration multi-grid lors des changements
  useEffect(() => {
    if (mounted) {
      try {
        localStorage.setItem(MULTIGRID_CONFIG_STORAGE_KEY, JSON.stringify(multiGridConfig))
      } catch (error) {
        // console.error('Erreur lors de la sauvegarde de la configuration multi-grid:', error)
      }
    }
  }, [multiGridConfig, mounted])

  // Wrapper pour setMainConfig avec callback support
  const setMainConfig = useCallback((config: AppConfig | ((prev: AppConfig) => AppConfig)) => {
    if (typeof config === 'function') {
      setMainConfigState(prev => {
        const newConfig = config(prev)
        return newConfig
      })
    } else {
      setMainConfigState(config)
    }
  }, [])

  // Wrapper pour setMultiGridConfig avec callback support
  const setMultiGridConfig = useCallback((config: MultiGridCanvasConfig | ((prev: MultiGridCanvasConfig) => MultiGridCanvasConfig)) => {
    if (typeof config === 'function') {
      setMultiGridConfigState(prev => {
        const newConfig = config(prev)
        return newConfig
      })
    } else {
      setMultiGridConfigState(config)
    }
  }, [])

  // Réinitialiser la configuration principale
  const resetMainConfig = useCallback(() => {
    setMainConfig(defaultMainConfig)
  }, [setMainConfig])

  // Réinitialiser la configuration multi-grid
  const resetMultiGridConfig = useCallback(() => {
    setMultiGridConfig(defaultMultiGridConfig)
  }, [setMultiGridConfig])

  // Sauvegarder la configuration actuelle avec un nom
  const saveCurrentConfig = useCallback(async (name?: string) => {
    try {
      // Combiner les deux configurations pour la sauvegarde
      const combinedConfig = {
        main: mainConfig,
        multiGrid: multiGridConfig
      }
      const result = await saveConfig(combinedConfig, { name: name || 'current' })
      if (!result.success) {
        // console.error('Erreur lors de la sauvegarde:', result.error)
      }
    } catch (error) {
      // console.error('Erreur lors de la sauvegarde de la configuration:', error)
    }
  }, [mainConfig, multiGridConfig, saveConfig])

  // Charger une configuration sauvegardée
  const loadSavedConfig = useCallback(async (name?: string) => {
    try {
      const result = await loadConfig(name || 'current')
      if (result.success && result.config) {
        if (result.config.main) {
          setMainConfig(prev => ({ ...prev, ...result.config.main }))
        }
        if (result.config.multiGrid) {
          setMultiGridConfig(prev => ({ ...prev, ...result.config.multiGrid }))
        }
        // Si c'est une ancienne configuration sans séparation, l'appliquer à la config principale
        else if (!result.config.main && !result.config.multiGrid) {
          setMainConfig(prev => ({ ...prev, ...result.config }))
        }
      }
    } catch (error) {
      // console.error('Erreur lors du chargement de la configuration:', error)
    }
  }, [loadConfig, setMainConfig, setMultiGridConfig])

  const value: ConfigContextType = {
    mainConfig,
    setMainConfig,
    multiGridConfig,
    setMultiGridConfig,
    resetMainConfig,
    resetMultiGridConfig,
    saveCurrentConfig,
    loadSavedConfig
  }

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  )
}

export function useConfig() {
  const context = useContext(ConfigContext)
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider')
  }
  return context
}
